// Styles
import {
  View,
  TouchableOpacity,
  StyleSheet,
  TextStyle,
  Image,
  ImageProps,
  Text,
  TextProps,
  TouchableOpacityProps,
  ViewStyle, ImageStyle,
  ViewProps,
  Platform
} from 'react-native'
import Loader from '../utility/loader'
import React, { ReactElement, useContext } from 'react'
import { ThemeContext } from '../utility/context/ThemeContext'
import useScreenSize from '../utility/screen-size'

type StyledTextProps = {
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'body' | 'label' | 'subscript'
} & TextProps

// { ✓ }
const CHECKMARK_LOGO = '\u007B\u2713\u007D'
export const CheckMarkLogo = (props: TextProps): ReactElement => (
  <Text {...props} >{CHECKMARK_LOGO}</Text>
)

export const StyledText = (props: StyledTextProps): ReactElement => {
  return (
    <Text
      {...props}
      style={{
       // fontsize, fontfamily, color
      ...textStyles[props.variant || 'body'],
      // other style properties provided by the user to override if needed
      ...(props.style as TextStyle || {}) 
      }}
    >
      {props.children}
    </Text>
  )
}

export const StyledImage = (props: ImageProps): ReactElement => {
  const { style, ...rest } = props

  const { width }= useScreenSize()

  return (
    <Image
      {...rest}
      resizeMode='contain'
      style={{
        width: width - 20,
        maxHeight: props.height || 0,
        resizeMode: 'contain',
        flex: 1,
        ...(style as ImageStyle || {})
      }}
    />
  )
}

export const IntroContainer = ({children}: ViewProps): React.ReactElement => (
  <View style={styles.introContainer}>{children}</View>
)

export const RowView = (props: ViewProps): ReactElement => {
  const { style, children, ...rest } = props
  return (
    <View
      {...rest}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        ...(style as ViewStyle || {})
      }}
    >
      {children}
    </View>
  )
}

interface ButtonProps {
  title: string
  onPress: () => void
  variant?: 'primary' | 'secondary' | 'link'
  size?: 'small' | 'medium' | 'large'
  loading?: boolean
  style?: ViewStyle
  textStyle?: TextStyle
  disabled?: boolean
}

export const StyledButton: React.FC<ButtonProps> = (props: ButtonProps) => {
  const { style, textStyle } = props
  const textProps = { ...props, style: textStyle || style }

  return (
    <ButtonWrapper {...props} disabled={props.loading || props.disabled}>
      {props.loading
        ? <Loader size="small" />
        : <ButtonText {...textProps}>{props.title}</ButtonText>
      }
    </ButtonWrapper>
  )
}

export const ButtonWrapper = (props: TouchableOpacityProps & ButtonProps): ReactElement => {
  const colors = useContext(ThemeContext).theme.colors
  return (
    <TouchableOpacity
      {...props}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        paddingHorizontal: props.size === 'small' ? 5 : props.size === 'large' ? 15 : 10,
        paddingVertical: props.size === 'small' ? 10 : props.size === 'large' ? 20 : 20,
        backgroundColor: props.variant === 'link' ? 'transparent' : colors[props.variant || 'primary'],
        borderRadius: 10,
        ...(props.style as ViewStyle || {}),
      }}
    >
      {props.children}
    </TouchableOpacity>
  )
}

export const ButtonText = (props: TextProps & ButtonProps): ReactElement => {
  return (
    <Text
      style={{
        color: props.variant === 'link' ? '#000' : '#fff',
        fontFamily: (props.style as TextStyle)?.fontFamily || 'poppins-regular',
        fontSize: props.size === 'small' ? 12 : props.size === 'large' ? 20 : 16,
        ...(props.style as TextStyle || {}),
      }}
    >
      {props.children}
    </Text>
  )
}

export const ShadowView = ({ children, ...props }: ViewProps): ReactElement => {
  const theme = useContext(ThemeContext).theme
  const shadowColor = theme?.colors?.shadowColor || '#000'

  const shadowStyle = Platform.select({
    ios: {
      shadowColor: shadowColor,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
    },
    android: {
      elevation: 5,
    },
    web: {
      boxShadow: `0px 0px 10px ${shadowColor}40`,
    }
  })

  return (
    <View
      {...props}
      style={[
        shadowStyle,
        props.style
      ]}
    >
      {children}
    </View>
  )
}

const styles = StyleSheet.create({
  introContainer: {
    backgroundColor: '#fff',
    flex: 1,
    paddingHorizontal: 10
  }
})

type FillerProps = {
  style?: ViewStyle
  children?: ReactElement | ReactElement[]
}

export const Filler = ({ style, children }: FillerProps): ReactElement => (
  <View style={[FillerStyles.fill, style]}>
    {children}
  </View>
)

const FillerStyles = StyleSheet.create({
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center'
  }  
})

export const ModalStyles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    height: '100%'
  },
  close: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 10
  },
  modal: {
    flex: 1,
    padding: 15,
    marginTop: '4%',
    marginHorizontal: 5,
    maxWidth: 600,
    borderRadius: 15,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,

    elevation: 5,
  },
  modalTitle: {
    textAlign: 'center',
    fontSize: 22,
    marginBottom: 15,
    fontWeight: 'bold'
  },
  modalBody: {
    fontSize: 14,
    marginBottom: 15,
  },
  modalButton: {
    backgroundColor: 'green',
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
    marginTop: 10,
    marginLeft: 'auto',
  }
})

export const textStyles = StyleSheet.create({
  h1: {
    fontSize: 28,
    fontFamily: 'poppins-semi-bold',
  },
  h2: {
    fontSize: 24,
    fontFamily: 'poppins-semi-bold',
  },
  h3: {
    fontSize: 20,
    fontFamily: 'poppins-regular',
  },
  h4: {
    fontSize: 16,
    fontFamily: 'poppins-regular',
  },
  body: {
    fontSize: 14,
    fontFamily: 'poppins-regular',
  },
  label: {
    fontSize: 12,
    fontFamily: 'poppins-regular',
  },
  subscript: {
    fontSize: 10,
    fontFamily: 'poppins-regular',
  }
})